import React, { useEffect, Fragment, useCallback, useState } from 'react';
import {
    Row,
    Col,
    Card,
    ListGroup,
    ListGroupItem,
    Container,
    UncontrolledButtonDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    Button
} from 'reactstrap';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import UserRelationGroupStatuses from '../../../../../enums/UserRelationGroupStatuses';
import UserRoles from '../../../../../enums/UserRoles';
import FilterStatuses from '../../../../../enums/FilterStatuses';
import api from '../../../../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../../Context/Auth/AuthContext';
import InputMask from 'react-input-mask';

export default function CoordinatorsList({
    id,
    activeTab,
    type,
    filter,
    newUser,
}) {
    const { toggleLoading, authenticated, currentGroup } = useAuth();
    const [coordinatorsData, setCoordinatorsData] = useState([]);

    const [currentMember, setCurrentMember] = useState(null);
    const [modalInactive, setModalInactive] = useState(false);
    const toggleModalInactive = useCallback((member = null) => {
        setCurrentMember(member);
        setModalInactive(!modalInactive);
    }, [setCurrentMember, setModalInactive, modalInactive]);

    const [modalRemove, setModalRemove] = useState(false);
    const toggleModalRemove = useCallback((member = null) => {
        setCurrentMember(member);
        setModalRemove(!modalRemove);
    }, [setCurrentMember, setModalRemove, modalRemove]);

    const [modalRemoveOwner, setModalRemoveOwner] = useState(false);
    const toggleModalRemoveOwner = useCallback(() => {
        setModalRemoveOwner(!modalRemoveOwner);
    }, [modalRemoveOwner, setModalRemoveOwner]);

    const [lastId, setLastId] = useState(null);

    const loadCoordinators = useCallback(() => {
        toggleLoading(true);
        api.post(`/ro.team.listCoordinatorsGroup/${id.id}`)
            .then((response) => {
                setCoordinatorsData(response.data.items);
                setLastId(response.data.lastId);
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
    }, [setCoordinatorsData, toggleLoading, id, setLastId]);

    const loadMore = useCallback(() => {
        toggleLoading(true);
        api.post(`/ro.team.listCoordinatorsGroup/${id.id}`, { lastId })
            .then((response) => {
                setCoordinatorsData([...coordinatorsData, ...response.data.items]);
                setLastId(response.data.lastId);
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
    }, [coordinatorsData, toggleLoading, id, setLastId, lastId]);

    useEffect(() => {
        if (activeTab === UserRoles.COORDINATOR.toString()) {
            loadCoordinators();
        }
        if (newUser === UserRoles.COORDINATOR.toString()) {
            loadCoordinators();
        }
    }, [activeTab, newUser, loadCoordinators]);

    const check = (member) => {
        if (filter.id === '') {
            return true;
        }

        if (
            filter.id === FilterStatuses[0].id &&
            (member.status.code ===
                UserRelationGroupStatuses.ACCEPTED_BY_PARENT ||
                member.status.code ===
                    UserRelationGroupStatuses.ACCEPTED_BY_CHILD)
        ) {
            return true;
        }

        if (
            filter.id === FilterStatuses[1].id &&
            (member.status.code ===
                UserRelationGroupStatuses.REQUESTED_BY_CHILD ||
                member.status.code ===
                    UserRelationGroupStatuses.INVITED_BY_PARENT)
        ) {
            return true;
        }

        if (
            filter.id === FilterStatuses[2].id &&
            member.status.code === UserRelationGroupStatuses.SUSPENDED
        ) {
            return true;
        }

        if (
            filter.id === FilterStatuses[3].id &&
            (member.status.code ===
                UserRelationGroupStatuses.REJECTED_BY_CHILD ||
                member.status.code ===
                    UserRelationGroupStatuses.REJECTED_BY_PARENT)
        ) {
            return true;
        }
    };

    const inactiveMember = useCallback(
        (member) => {
            toggleLoading(true);
            api.patch(`/ro.team.inactivateMember/${id.id}`, {
                type: UserRoles.COORDINATOR,
                idMember: member.id,
            })
                .then((response) => {
                    toast.success(`${member.name} is marked as inactive`);
                    toggleModalInactive(null);
                    loadCoordinators();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        },
        [id, toggleLoading, toggleModalInactive, loadCoordinators]
    );

    const userInfo = useCallback(() => {
        if (authenticated.user) {
            return authenticated.user;
        } else {
            return authenticated;
        }
    }, [authenticated]);

    const removeMember = useCallback(
        (member) => {
            if (userInfo().id === member.id) {
                toast.error('You can only be removed as Coordinator of the group after you transfer the group to another Coordinator');
                return;
            }

            toggleLoading(true);
            api.patch(`/ro.team.removeMember/${id.id}`, {
                type: UserRoles.COORDINATOR,
                idMember: member.id,
            })
                .then((response) => {
                    toast.success(`${member.name} was removed!`);
                    toggleModalRemove(null);
                    loadCoordinators();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        },
        [id, toggleLoading, userInfo, toggleModalRemove, loadCoordinators]
    );

    const resendInvite = useCallback(
        (member) => {
            toggleLoading(true);
            api.post(`/ro.team.resendInvite/`, {
                gid: id.id,
                user: member.id,
            })
                .then((response) => {
                    toast.success(`${member.name} was invited again!`);
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        },
        [id, toggleLoading]
    );

    const activeMember = useCallback(
        (member) => {
            api.patch(`/ro.team.activateMember/${id.id}`, {
                type: UserRoles.COORDINATOR,
                idMember: member.id,
            })
                .then((response) => {
                    toast.success(`${member.name} has been activated!`);
                    loadCoordinators();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                });
        },
        [id, loadCoordinators]
    );

    const checkIsGroupOwner = useCallback(() => {
        let isOwner = false;

        if(currentGroup) {    
            currentGroup.roles.map((role) => {
                if (role.code === 4) {
                    isOwner = true;
                }
                return true;
            });
        }

        return isOwner;
    }, [currentGroup]);

    return (
        <Fragment>
            <Container fluid>
                <Row>
                    <Col lg="12">
                        <Card className="main-card mb-3">
                            <ListGroup flush className="users-list">
                                {coordinatorsData &&
                                    coordinatorsData.map(
                                        (member) =>
                                            check(member) && (
                                                <ListGroupItem key={member.id}>
                                                    <div className="widget-content p-0">
                                                        <div className="widget-content-wrapper">
                                                            <div className="widget-content-left mr-3">
                                                                <div className="placeholder-avatar">
                                                                    {member.name
                                                                        ? member.name.charAt(
                                                                              0
                                                                          )
                                                                        : member.email.charAt(
                                                                              0
                                                                          )}
                                                                </div>
                                                            </div>
                                                            <div className="widget-content-left">
                                                                <div className="widget-heading">
                                                                    {member.name &&
                                                                        member.name}
                                                                    {member.isOwner && (
                                                                        <div className="mb-2 ml-2 badge badge-info">Group Owner</div>
                                                                    )}
                                                                </div>
                                                                <div className="widget-subheading">
                                                                    {
                                                                        member.email
                                                                    }
                                                                </div>
                                                                <div className="widget-subheading">
                                                                    {member.phone && (
                                                                        <InputMask
                                                                            mask="(999) 999-9999"
                                                                            disabled={true}
                                                                            defaultValue={member.phone}
                                                                            className="textInputChange"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="widget-content-right">
                                                                {member.status
                                                                    .code ===
                                                                    UserRelationGroupStatuses.INVITED_BY_PARENT && (
                                                                    <div className="mb-2 mr-2 badge badge-warning">
                                                                        Waiting
                                                                        for
                                                                        acceptance
                                                                    </div>
                                                                )}

                                                                {member.status
                                                                    .code ===
                                                                    UserRelationGroupStatuses.SUSPENDED && (
                                                                    <div className="mb-2 mr-2 badge badge-info">
                                                                        Inactive
                                                                    </div>
                                                                )}

                                                                {member.status
                                                                    .code ===
                                                                    UserRelationGroupStatuses.REJECTED_BY_CHILD && (
                                                                    <div className="mb-2 mr-2 badge badge-info">
                                                                        Declined
                                                                    </div>
                                                                )}

                                                                {checkIsGroupOwner() && (
                                                                    <UncontrolledButtonDropdown direction="left">
                                                                        <DropdownToggle color="primary">
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faBars
                                                                                }
                                                                                size="1x"
                                                                            />
                                                                        </DropdownToggle>
                                                                        <DropdownMenu className="no-arrow">
                                                                            <DropdownItem
                                                                                header
                                                                            >
                                                                                Actions
                                                                            </DropdownItem>
                                                                            <DropdownItem
                                                                                divider
                                                                            />
                                                                            {member
                                                                                .status
                                                                                .code ===
                                                                                UserRelationGroupStatuses.SUSPENDED &&(
                                                                                <DropdownItem
                                                                                    onClick={() => {
                                                                                        activeMember(
                                                                                            member
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    Mark
                                                                                    as
                                                                                    Active
                                                                                </DropdownItem>
                                                                            )}
                                                                            {member
                                                                                .status
                                                                                .code !==
                                                                                UserRelationGroupStatuses.SUSPENDED && (member.isOwner === false) && (
                                                                                <DropdownItem
                                                                                    onClick={() => {
                                                                                        toggleModalInactive(member);
                                                                                        // inactiveMember(
                                                                                        //     member
                                                                                        // );
                                                                                    }}
                                                                                >
                                                                                    Mark
                                                                                    as
                                                                                    Inactive
                                                                                </DropdownItem>
                                                                            )}
                                                                            {userInfo().id !== member.id && (
                                                                                <DropdownItem
                                                                                    onClick={() => {
                                                                                        toggleModalRemove(member);
                                                                                        // removeMember(
                                                                                        //     member
                                                                                        // );
                                                                                    }}
                                                                                >
                                                                                    Remove
                                                                                    Coordinator
                                                                                </DropdownItem>
                                                                            )}
                                                                            {userInfo().id === member.id && (
                                                                                <DropdownItem
                                                                                    onClick={toggleModalRemoveOwner}
                                                                                >
                                                                                    Remove
                                                                                    Coordinator
                                                                                </DropdownItem>
                                                                            )}
                                                                            
                                                                            {member
                                                                                .status
                                                                                .code !==
                                                                                UserRelationGroupStatuses.ACCEPTED_BY_CHILD && 
                                                                                member
                                                                                .status
                                                                                .code !==
                                                                                UserRelationGroupStatuses.ACCEPTED_BY_PARENT &&(
                                                                                <DropdownItem
                                                                                    onClick={() => {
                                                                                        resendInvite(
                                                                                            member
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    Resend
                                                                                    Invite
                                                                                </DropdownItem>
                                                                            )}
                                                                        </DropdownMenu>
                                                                    </UncontrolledButtonDropdown>
                                                                )}

                                                            </div>
                                                        </div>
                                                    </div>
                                                </ListGroupItem>
                                            )
                                    )}
                            </ListGroup>
                        </Card>

                        {lastId !== null && (
                            <div className="text-center">
                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="info"
                                    onClick={loadMore}
                                >
                                    Load more
                                </Button>
                            </div>
                        )}

                    </Col>
                </Row>

                <Modal isOpen={modalRemove} toggle={toggleModalRemove}>
                    <ModalHeader toggle={toggleModalRemove}>
                        Confirm remove Coordinator
                    </ModalHeader>
                    <ModalBody>
                        <div className="text-center">
                            <p>Do you really want to remove Coordinator?</p>
                            <Button
                                className="btn-wide mb-2 mr-2"
                                size="md"
                                color="danger"
                                onClick={() => {
                                    removeMember(currentMember);
                                }}
                            >
                                Remove
                            </Button>

                            <Button
                                className="btn-wide mb-2 mr-2"
                                size="md"
                                color="secondary"
                                onClick={toggleModalRemove}
                            >
                                Cancel
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>

                <Modal isOpen={modalRemoveOwner} toggle={toggleModalRemoveOwner}>
                    <ModalHeader toggle={toggleModalRemoveOwner}>
                        You can't remove yourself
                    </ModalHeader>
                    <ModalBody>
                        <div className="text-center">
                            <p>You can only be removed as Coordinator of the group after you transfer the group to another Coordinator</p>
                            <Button
                                className="btn-wide mb-2 mr-2"
                                size="md"
                                color="secondary"
                                onClick={toggleModalRemoveOwner}
                            >
                                OK
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>

                <Modal isOpen={modalInactive} toggle={toggleModalInactive}>
                    <ModalHeader toggle={toggleModalInactive}>
                        Confirm Inactive Coordinator
                    </ModalHeader>
                    <ModalBody>
                        <div className="text-center">
                            <p>Do you really want to inactivate Coordinator?</p>
                            <Button
                                className="btn-wide mb-2 mr-2"
                                size="md"
                                color="danger"
                                onClick={() => {
                                    inactiveMember(currentMember);
                                }}
                            >
                                Inactive
                            </Button>

                            <Button
                                className="btn-wide mb-2 mr-2"
                                size="md"
                                color="secondary"
                                onClick={toggleModalInactive}
                            >
                                Cancel
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>

            </Container>
        </Fragment>
    );
}
